import { Form } from "antd";
import { useAppDispatch } from "core/utils";
import i18n from "i18next";
import type { FC } from "react";

import { StyledInput, ValidationNoInput } from "src/app/features/ApplicationConfigurations/components/validations/ValidationInputs";
import type { ValidationMapType } from "src/app/features/ApplicationConfigurations/types/AppConfigsTypes";
import { SecondaryButton } from "../../../../../components/Buttons/components/ActionButton";
import { getValidationRules } from "../../../../../components/Form/components/Validations/ValidationFieldsFactory";
import { TextInput } from "../../../../../components/Form/formComponents";
import { NumberInput } from "../../../../../components/Form/formComponents/Number/NumberInput";
import type { FormError, ValidationRule } from "../../../../../components/Form/types/types";
import { ValidationType } from "../../../../../components/Form/types/types";
import { showWarningNotification } from "../../../../../components/NotificationAlerts";

export interface ValidationFormConfigProps {
    value: ValidationType;
    onChange: (input: ValidationRule) => void;
}

export const ValidationFormConfig: FC<ValidationFormConfigProps> = ({ value, onChange }) => {
    const dispatch = useAppDispatch();

    const [form] = Form.useForm();

    const validationMap: Record<ValidationType, ValidationMapType> = {
        [ValidationType.VALIDATION_REQUIRED]: <ValidationNoInput />,
        [ValidationType.VALIDATION_MIN]: <NumberInput label="" name="" readOnly={false} />,
        [ValidationType.VALIDATION_MAX]: <NumberInput label="" name="" readOnly={false} />,
        [ValidationType.VALIDATION_MAX_LENGTH]: <NumberInput label="" name="" readOnly={false} />,
        [ValidationType.VALIDATION_MIN_LENGTH]: <NumberInput label="" name="" readOnly={false} />,
        [ValidationType.PATTERN]: <TextInput label="" name="" readOnly={false} />,
    };

    const validationInput = (validation: ValidationType) => {
        form.resetFields();
        return validationMap[validation];
    };

    const submitForm = () => {
        const validationParams = form.getFieldValue("validationParams");
        onChange({ name: value, params: [validationParams] });
    };

    const onSubmit = () => {
        form.validateFields()
            .then(() => submitForm())
            .catch((err: FormError) => {
                dispatch(
                    showWarningNotification({
                        text: `There are ${err.errorFields.length} validation errors:
                        ${err.errorFields.map((field) => `\n ${field.name.join(".")}: ${field.errors.join("; ")}`).join("")}`,
                        autoCloseDisabled: true,
                    }),
                );
            });
    };

    // TODO AP3ADMIN-2215 configure relationship between linked validations - e.g. min and max cannot be in opposition to eachother
    const getRules = () => {
        return value === ValidationType.VALIDATION_REQUIRED
            ? []
            : [
                  {
                      name: ValidationType.VALIDATION_REQUIRED,
                  },
                  {
                      name: ValidationType.VALIDATION_MAX_LENGTH,
                      params: [64],
                  },
              ];
    };

    return (
        value && (
            <>
                <Form form={form} initialValues={{}}>
                    <Form.Item
                        shouldUpdate={(prevValues, currentValues) => prevValues !== currentValues}
                        name="validationParams"
                        rules={getValidationRules(getRules())}
                    >
                        {validationInput(value)}
                    </Form.Item>
                </Form>
                <StyledInput>
                    <SecondaryButton onClick={onSubmit} disabled={false}>
                        {i18n.t("button.add")}
                    </SecondaryButton>
                </StyledInput>
            </>
        )
    );
};
